import React, { useCallback, useEffect, useRef, useState } from "react";
import SelectDateSlotSection from "./PrescribedDiagnosticsComponents/SelectDateSlotSection";
import CartDashboardUtils from "../services/CartDashboardUtils";
import NavigoDashboardUtils from "../../navigo-module/services/NavigoDashboardUtils";
import EventService from "../../../../../service/event/EventService";
import "./../styles/PrescribedDiagnosticsStyles.scss";

// Reschedule for a CART appointment: the service provider is pinned, only the date
// and time slot can move.
//
// A cart appointment's serviceID is a GROUP service, which has no provider mapping of
// its own (providers map to its child services). So neither of the endpoints the
// regular reschedule dialog uses works here: the provider fetch comes back empty and
// /v1/appointment/available-slots 500s. Slots must come from the range endpoint
// (CartDashboardUtils.getSlots), which resolves the provider from childServiceIds.
const CartRescheduleDialog = (props) => {
  const { appointment, open, onClose, onRescheduled, onBusyChange } = props;

  const [slotData, setSlotData] = useState(null);

  const cartDashboardUtils = useRef(new CartDashboardUtils()).current;
  const navigoDashboardUtils = useRef(new NavigoDashboardUtils()).current;

  // The appointment detail screen can unmount mid-flight (exitPage), and the Continue
  // button has no disabled prop for the parent to drive — so both are tracked by ref.
  const mountedRef = useRef(true);
  const submitInFlightRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const setBusy = useCallback(
    (busy) => {
      if (onBusyChange) onBusyChange(busy);
    },
    [onBusyChange],
  );

  // A day with no slots still gets a dates[] entry (with an empty slots list), so an
  // entirely booked-out window has dates.length > 0. Emptiness has to be read off the
  // slots map, or we'd open a picker whose every date chip is disabled.
  const hasAnySlot = (data) =>
    Object.values(data?.slots || {}).some(
      (slots) => Array.isArray(slots) && slots.length > 0,
    );

  useEffect(() => {
    if (!open || appointment == null) return;

    setSlotData(null);
    setBusy(true);
    cartDashboardUtils.getSlots(
      {
        serviceProviderID: appointment.serviceProviderID,
        planId: appointment.planID,
        patientId: appointment.patientID,
        bookedFor: appointment.bookedFor,
        serviceId: appointment.serviceID,
        childServiceIds: appointment.childServiceIds,
        addressId: appointment.addressId,
      },
      (res) => {
        if (!mountedRef.current) return;
        setBusy(false);
        if (!hasAnySlot(res)) {
          EventService.emit("showError", {
            message: `No slots available for this service provider`,
          });
          onClose();
          return;
        }
        setSlotData(res);
      },
      (err) => {
        if (!mountedRef.current) return;
        setBusy(false);
        EventService.emit("showError", {
          message: `Error: ${err?.message}`,
        });
        onClose();
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, appointment?.id]);

  const isCurrentSlot = (date, slot) =>
    String(appointment?.appointmentDate ?? "").substring(0, 10) ===
      date?.dayDate && slot?.value === appointment?.timeSlot;

  const handleConfirm = (date, slot) => {
    if (submitInFlightRef.current) return;

    // The appointment's existing slot is pre-selected, so Continue is live the moment
    // the dialog opens. Rescheduling to the slot it is already on is not a no-op on the
    // backend — it re-validates, rewrites the doc, writes an audit entry and re-books
    // with the vendor — so swallow it here.
    if (isCurrentSlot(date, slot)) {
      onClose();
      return;
    }

    submitInFlightRef.current = true;
    setBusy(true);

    const body = {
      appointmentID: appointment?.id,
      appointmentDate: `${date.dayDate}T00:00:00.000Z`,
      timeslot: slot.value,
      timeSlotId: slot.key,
      // Re-sending the appointment's own provider is what pins it. (Sending 0 would also
      // keep it, but this way the audit log records the provider as unchanged.)
      serviceProviderID: appointment?.serviceProviderID,
    };

    navigoDashboardUtils.rescheduleAppointment(
      body,
      (res) => {
        if (!mountedRef.current) return;
        setBusy(false);
        if (res?.id == null) {
          submitInFlightRef.current = false;
          EventService.emit("showError", {
            message: `Unable to reschedule appointment`,
          });
          return;
        }
        EventService.emit("showError", {
          message: `Appointment rescheduled successfully`,
        });
        onClose();
        if (onRescheduled) onRescheduled();
      },
      (err) => {
        if (!mountedRef.current) return;
        // Leave the picker open: the usual failure here is the slot being taken between
        // fetch and submit, and the admin should be able to pick another one right away.
        submitInFlightRef.current = false;
        setBusy(false);
        EventService.emit("showError", {
          message: `Error: ${err?.message}`,
        });
      },
    );
  };

  if (!open || slotData == null) return null;

  return (
    <SelectDateSlotSection
      openSelectDateSlot={open}
      setOpenSelectDateSlot={(visible) => {
        if (!visible) onClose();
      }}
      slotData={slotData}
      onConfirm={handleConfirm}
      requestedSlot={{
        date: appointment?.appointmentDate,
        time: appointment?.timeSlot,
        // timeSlotId isn't persisted server-side and can come back 0, which would match
        // a slot whose key is 0. Nulling it falls back to matching on the time value.
        id: appointment?.timeSlotId || null,
      }}
      showRequestedBanner={false}
      autoSelectFirstAvailable={true}
    />
  );
};

export default CartRescheduleDialog;
